/* ============================================================================
   Studio settings — the manager passphrase STATUS. The hash itself now lives
   only server-side (verified via /api/auth/manager); the synced row just says
   whether a passphrase is set. `passphraseHash` remains solely for plain
   local dev, where there is no worker to verify against.
   ========================================================================== */
import { useEffect, useState } from "react";

const KEY = "atelier.settings.v1";
export const SETTINGS_AGG_ID = "studio-settings";

export interface StudioSettings {
  id: string;                 // SETTINGS_AGG_ID
  passphraseSet?: boolean;    // server-verified passphrase exists
  passphraseHash?: string;    // LOCAL-DEV ONLY fallback — never synced any more
  passphraseSetAt?: string;
}

/** Is manager sign-in passphrase-gated (server-set or local legacy)? */
export function passphraseActive(s: StudioSettings): boolean {
  return !!(s.passphraseSet || s.passphraseHash);
}

function load(): StudioSettings {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return { id: SETTINGS_AGG_ID };
}

let state: StudioSettings = load();
const subs = new Set<() => void>();

function commit(next: StudioSettings) {
  state = next;
  try { localStorage.setItem(KEY, JSON.stringify(state)); } catch { /* quota */ }
  subs.forEach((f) => f());
  changeListener?.(state);
}

export function useSettings(): StudioSettings {
  const [, force] = useState(0);
  useEffect(() => {
    const f = () => force((n) => n + 1);
    subs.add(f);
    return () => { subs.delete(f); };
  }, []);
  return state;
}
export function getSettings() { return state; }

/* --- sync hooks --------------------------------------------------------- */
let changeListener: ((s: StudioSettings) => void) | null = null;
export function onSettingsChange(f: (s: StudioSettings) => void) { changeListener = f; }
export function getSettingsAggregates(): StudioSettings[] { return [state]; }
export function replaceSettingsAggregates(aggs: { id: string }[]) {
  const row = aggs.find((a) => a.id === SETTINGS_AGG_ID) as StudioSettings | undefined;
  if (!row) return;
  state = row;
  try { localStorage.setItem(KEY, JSON.stringify(state)); } catch { /* quota */ }
  subs.forEach((f) => f());
}

/* --- actions --------------------------------------------------------------- */
export const settingsActions = {
  /** Local-dev fallback only — production passphrases are set via /api/auth. */
  setPassphraseHash(hash: string | undefined) {
    commit({ ...state, passphraseHash: hash, passphraseSet: !!hash, passphraseSetAt: hash ? new Date().toISOString() : undefined });
  },
  /** Reflect a server-side set/clear locally (the server holds the hash). */
  markPassphraseSet(set: boolean) {
    commit({ id: SETTINGS_AGG_ID, passphraseSet: set, passphraseSetAt: set ? new Date().toISOString() : undefined });
  },
};

export async function sha256Hex(text: string): Promise<string> {
  const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(text));
  return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, "0")).join("");
}
